// src/components/views/NFTMyNFTsView.js
// (updated – Prospect artwork & naming + badge modal support + bridge select mode)

import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Spinner, Button } from 'react-bootstrap';
import LazyLoad from 'react-lazyload';
import ScanForNFTs from '../ScanForNFTs';
import { getApiBaseUrl } from '../../utils/apiConfig';

const PROSPECT_IMG =
  'https://nft.sogni.ai/big/badge-fast-worker-prospect/001.jpg';

/**
 * Badge collections that should open an in‑app modal
 * instead of navigating away.
 */
const SPECIAL_BADGE_SETS = new Set([
  'Sogni AI OG Badge',
  'Testnet Warrior – Fast',
  'Testnet Warrior – Relaxed',
  'Testnet Warrior – Artist'
]);

const NFTMyNFTsView = ({
  nfts = [],
  badges = [],
  account,
  handleCardClick,
  handleBadgeClick,
  baseURI,
  publicContract,
  sogniClient,
  // Bridge props
  bridgeSelectMode = false,
  selectedNFTs,
  sourceChain,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onEnterBridgeMode,
  onExitBridgeMode,
  refreshTrigger = 0,
  bridgedTokenKeys,
}) => {
  const sessionWallet = sogniClient?.account?.currentAccount?.walletAddress || '';
  const ownerAddress = account || sessionWallet;
  const accountLc = ownerAddress ? ownerAddress.toLowerCase() : '';

  const [apiNfts, setApiNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scannedNFTs, setScannedNFTs] = useState([]);

  const handleScannedNFTs = (nfts) => {
    setScannedNFTs(nfts);
  };

  /* ---------- fetch wallet‑held worker NFTs ---------- */
  useEffect(() => {
    if (!accountLc) return;
    let cancelled = false;

    const fetchOwned = async () => {
      setLoading(true);
      setError('');
      try {
        const baseUrl = getApiBaseUrl();
        const etherlinkContract = process.env.REACT_APP_ETHERLINK_NFT_CONTRACT;

        // Fetch Base worker NFTs
        const baseRes = await fetch(`${baseUrl}/nfts?owner=${accountLc}`);
        if (!baseRes.ok) throw new Error(`API returned ${baseRes.status}`);
        const baseRaw = await baseRes.json();
        const baseEnriched = Object.keys(baseRaw).map((idStr) => ({
          tokenId: parseInt(idStr, 10),
          minted: true,
          owner: baseRaw[idStr].owner,
          imageSrc: PROSPECT_IMG,
          chain: 'Base'
        }));

        // Fetch Etherlink worker NFTs (if configured)
        let etherlinkEnriched = [];
        if (etherlinkContract) {
          try {
            const ethRes = await fetch(
              `${baseUrl}/nfts?contract=${etherlinkContract}&owner=${accountLc}`
            );
            if (ethRes.ok) {
              const ethRaw = await ethRes.json();
              etherlinkEnriched = Object.keys(ethRaw).map((idStr) => ({
                tokenId: parseInt(idStr, 10),
                minted: true,
                owner: ethRaw[idStr].owner,
                imageSrc: PROSPECT_IMG,
                chain: 'Etherlink'
              }));
            }
          } catch (e) {
            console.error('Etherlink MyNFTs fetch error:', e);
          }
        }

        if (!cancelled) setApiNfts([...baseEnriched, ...etherlinkEnriched]);
      } catch (e) {
        console.error('MyNFTs API error:', e);
        if (!cancelled) setError('Failed to load your NFTs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOwned();
    return () => {
      cancelled = true;
    };
  }, [accountLc, refreshTrigger]);

  /* ---------- merge gallery + API worker NFTs ---------- */
  const galleryOwnedWorkers = useMemo(() => {
    if (!accountLc) return [];
    return nfts
      .filter((n) => n.minted && n.owner?.toLowerCase() === accountLc)
      .map((n) => ({
        ...n,
        imageSrc: PROSPECT_IMG
      }));
  }, [accountLc, nfts]);

  const ownedWorkers = useMemo(() => {
    // Use chain-aware key to avoid collisions (same tokenId on Base + Etherlink)
    const byKey = new Map();
    galleryOwnedWorkers.forEach((w) => byKey.set(`${w.chain || 'Base'}-${w.tokenId}`, w));
    apiNfts.forEach((w) => byKey.set(`${w.chain || 'Base'}-${w.tokenId}`, w));
    // Etherlink-priority dedup: if a tokenId exists on Etherlink, remove the Base entry
    // (the NFT is locked in the bridge contract on Base, so it shouldn't show there)
    byKey.forEach((_, key) => {
      if (key.startsWith('Base-')) {
        const tokenId = key.slice(5);
        if (byKey.has(`Etherlink-${tokenId}`)) byKey.delete(key);
      }
    });
    // Filter out NFTs that were just bridged from the source chain (optimistic update)
    if (bridgedTokenKeys?.size > 0) {
      bridgedTokenKeys.forEach((key) => byKey.delete(key));
    }
    return Array.from(byKey.values()).sort((a, b) => a.tokenId - b.tokenId);
  }, [galleryOwnedWorkers, apiNfts, bridgedTokenKeys]);

  /* ---------- bridge selection helpers ---------- */
  const isSelected = (nft) => {
    if (!selectedNFTs) return false;
    const key = `${nft.chain || 'Base'}-${nft.tokenId}`;
    return selectedNFTs.has(key);
  };

  const isDisabledForBridge = (nft) => {
    if (!bridgeSelectMode || !sourceChain) return false;
    const chain = nft.chain || 'Base';
    return chain !== sourceChain;
  };

  /* ---------- card helper ---------- */
  const renderWorkerCard = (nft) => {
    const chain = nft.chain || 'Base';
    const key = `worker-${chain}-${nft.tokenId}`;
    const title = `Sogni Prospect Worker #${nft.tokenId} (${chain})`;
    const selected = isSelected(nft);
    const disabled = isDisabledForBridge(nft);

    const handleClick = () => {
      if (bridgeSelectMode) {
        if (!disabled && onToggleSelection) onToggleSelection(nft);
      } else {
        handleCardClick(nft, { preventNavigate: true });
      }
    };

    return (
      <Col key={key} xs={6} sm={6} md={4} lg={3} className="mb-4">
        <div
          className={`nft-card ${selected ? 'nft-card-selected' : ''} ${disabled ? 'nft-card-disabled' : ''}`}
          style={{ backgroundColor: '#222', color: '#fff', cursor: 'pointer' }}
          onClick={handleClick}
        >
          <div style={{ position: 'relative' }}>
            <LazyLoad height={200} offset={0} once>
              <img
                src={PROSPECT_IMG}
                alt={title}
                style={{ width: '100%', borderRadius: '10px' }}
              />
            </LazyLoad>
            <div className="overlay">MINTED</div>

            {/* Bridge select checkbox */}
            {bridgeSelectMode && !disabled && (
              <div className="bridge-checkbox">
                {selected ? (
                  <span className="bridge-checkbox-checked">&#10003;</span>
                ) : (
                  <span className="bridge-checkbox-unchecked" />
                )}
              </div>
            )}
          </div>
          <div style={{ padding: '10px' }}>
            <h5 style={{ color: '#39ff14' }}>{title}</h5>
          </div>
        </div>
      </Col>
    );
  };

  const renderCard = ({ key, imageSrc, title, overlay, onClick }) => (
    <Col key={key} xs={6} sm={6} md={4} lg={3} className="mb-4">
      <div
        className="nft-card"
        style={{ backgroundColor: '#222', color: '#fff', cursor: 'pointer' }}
        onClick={onClick}
      >
        <div style={{ position: 'relative' }}>
          <LazyLoad height={200} offset={0} once>
            <img
              src={imageSrc}
              alt={title}
              style={{ width: '100%', borderRadius: '10px' }}
            />
          </LazyLoad>
          <div className="overlay">{overlay}</div>
        </div>
        <div style={{ padding: '10px' }}>
          <h5 style={{ color: '#39ff14' }}>{title}</h5>
        </div>
      </div>
    </Col>
  );

  /* ---------- UI states ---------- */
  if (!accountLc) return <p>Please log in to Sogni to view your NFTs.</p>;
  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spinner animation="border" variant="light" />
      </div>
    );
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  const hasWorkerNFTs = ownedWorkers.length > 0;

  return (
    <>
      {/* ---------- Bridge mode header ---------- */}
      {hasWorkerNFTs && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            {bridgeSelectMode && (
              <span style={{ color: '#b5ff16', fontSize: '0.9rem' }}>
                Select NFTs to bridge {sourceChain ? `(${sourceChain} only)` : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {!bridgeSelectMode ? (
              <Button
                variant="outline-success"
                size="sm"
                onClick={onEnterBridgeMode}
                style={{ fontSize: '0.85rem' }}
              >
                Bridge NFTs
              </Button>
            ) : (
              <>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    if (selectedNFTs?.size > 0 && selectedNFTs?.size === ownedWorkers.length) {
                      onClearSelection?.();
                    } else {
                      onSelectAll?.(ownedWorkers);
                    }
                  }}
                  style={{ fontSize: '0.85rem' }}
                  disabled={ownedWorkers.length === 0}
                >
                  {selectedNFTs?.size > 0 && selectedNFTs?.size === ownedWorkers.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={onExitBridgeMode}
                  style={{ fontSize: '0.85rem' }}
                >
                  Cancel Bridge
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <Row>
        {/* ---------- Prospect Worker NFTs ---------- */}
        {ownedWorkers.map((nft) => renderWorkerCard(nft))}

        {/* ---------- Badge NFTs (hidden in bridge select mode) ---------- */}
        {!bridgeSelectMode &&
          badges.map((badge, idx) =>
            renderCard({
              key: `badge-${idx}`,
              imageSrc: badge.smallUrl,
              title:
                badge.collectionName === 'Sogni AI OG Badge' || !badge.tokenId
                  ? badge.collectionName
                  : `${badge.collectionName} #${badge.tokenId}`,
              overlay: 'BADGE',
              onClick: () =>
                SPECIAL_BADGE_SETS.has(badge.collectionName)
                  ? handleBadgeClick(badge)
                  : window.open(badge.bigUrl, '_blank', 'noopener,noreferrer')
            })
          )}

        {/* ---------- Scanned Base NFTs (hidden in bridge select mode) ---------- */}
        {!bridgeSelectMode &&
          scannedNFTs.map((nft) =>
            renderCard({
              key: `scanned-${nft.tokenId}`,
              imageSrc: nft.image || nft.animationUrl || 'https://via.placeholder.com/400',
              title: nft.name || `NFT #${nft.tokenId}`,
              overlay: '3RD PARTY',
              onClick: () => handleCardClick({
                ...nft,
                isScannedNFT: true,
                animationUrl: nft.animationUrl ? 'http://green-proud-tarsier-176.mypinata.cloud/ipfs/bafybeiaclwjriat4fbrl4vyzoh5dbgax7p3rtl7sgeppbdf2mkl3jxlwy4' : null
              }, { preventNavigate: true })
            })
          )}

        {/* ---------- Scan for NFTs Card (hidden in bridge select mode) ---------- */}
        {!bridgeSelectMode && <ScanForNFTs onNFTFound={handleScannedNFTs} />}
      </Row>
    </>
  );
};

export default NFTMyNFTsView;
